import { createElement, useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { Button as MuiButton, Divider, Grid, Stack } from '@mui/material';

import { MasterIndexApi } from '@/api';
import {
  EmailsForm,
  FormRoot,
  MasterNameAutocomplete,
  MultiAddressForm,
  NoResultItem,
  RenderFormField,
  ViewItem,
} from '@/components';
import { Button } from '@/components/TailwindComponents';
import { MasterNameFormData } from '@/layouts/TabLayout/MasterNames/components/MasterNameForm';
import { MasterNamePhoneNumberForm } from '@/layouts/TabLayout/MasterNames/components/MasterNamePhoneNumberForm';
import {
  EmergencyContactFormFields,
  EmergencyContactViewFields,
} from '@/layouts/TabLayout/MasterNames/data';
import {
  AddNewEmergencyContactForm,
  IEmergencyContact,
  IMasterName,
  IMasterNamePerson,
  MasterNameType,
} from '@/models';
import { getAddressLocation, isValidStructuredAddress } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { parsePhoneNumber } from '@/utils';

const specialFields: Record<string, React.ComponentType<any>> = {
  phoneNumbers: MasterNamePhoneNumberForm,
  addresses: MultiAddressForm,
  emails: EmailsForm,
};

interface EmergencyContactFormProps {
  onClose: () => void;
  currentEmergencyContact?: IEmergencyContact;
}

const normalizeEmails = (
  emailsValue: string[] | { email: string }[] | undefined,
): string[] => {
  if (!Array.isArray(emailsValue)) {
    return [];
  }

  if (emailsValue[0] && typeof emailsValue[0] === 'string') {
    return emailsValue as string[];
  }

  return (emailsValue as { email: string }[])
    .map((e) => e.email)
    .filter((email) => email !== '');
};

export const EmergencyContactForm = ({
  onClose,
  currentEmergencyContact,
}: EmergencyContactFormProps) => {
  const methods = useForm<AddNewEmergencyContactForm>({
    defaultValues: {
      emails: [{ email: '' }],
    },
  });
  const { updateToast } = useToastStore();

  const [isMasterNameForm, setIsMasterNameForm] = useState(false);
  const [existingMasterName, setExistingMasterName] =
    useState<IMasterNamePerson | null>(null);

  const { watch, setValue, getValues } = useFormContext<MasterNameFormData>();

  const emergencyContactList = watch('emergencyContacts');

  useEffect(() => {
    if (
      !currentEmergencyContact?.emergencyContact ||
      typeof currentEmergencyContact.emergencyContact === 'string'
    ) {
      return;
    }

    setExistingMasterName(currentEmergencyContact.emergencyContact);
    methods.reset({
      phoneNumbers: currentEmergencyContact.emergencyContact.phoneNumbers?.map(
        ({ type, phoneNumber }) => ({
          type,
          phoneNumber: phoneNumber?.replace(/^\+1/, '') ?? '',
        }),
      ),
      firstName: currentEmergencyContact.emergencyContact.firstName,
      lastName: currentEmergencyContact.emergencyContact.lastName,
      middleName: currentEmergencyContact.emergencyContact.middleName,
      addresses: currentEmergencyContact.emergencyContact.addresses,
      relationship:
        currentEmergencyContact.relationship?.relationship ?? undefined,
      emails:
        Array.isArray(currentEmergencyContact.emergencyContact.emails) &&
        currentEmergencyContact.emergencyContact.emails.length > 0
          ? currentEmergencyContact.emergencyContact.emails.map((email) =>
              typeof email === 'string' ? { email } : { email: '' },
            )
          : [{ email: '' }],
      isPrimary: currentEmergencyContact.relationship?.isPrimary ?? undefined,
    });
  }, [currentEmergencyContact]);

  const handleAddNewSubmit = async (values: AddNewEmergencyContactForm) => {
    const filteredAddresses = values.addresses?.filter((address) =>
      isValidStructuredAddress(address),
    );

    const parsedPhoneNumbers = values.phoneNumbers
      ?.filter(({ type, phoneNumber }) => {
        return type && phoneNumber !== '';
      })
      .map(({ type, phoneNumber }) => ({
        type,
        phoneNumber: parsePhoneNumber(phoneNumber, '+1'),
      }));

    const masterNamePayload = {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      address: '',
      addresses: filteredAddresses,
      phoneNumbers: parsedPhoneNumbers,
      emails: normalizeEmails(values.emails),
    };

    try {
      const response = await MasterIndexApi.createMasterPerson(
        masterNamePayload,
      );
      const { _id } = response.data;

      if (_id) {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message:
            'Emergency contact successfully added. Remember to save the person record to finalize the update.',
        });

        const emergencyContact = {
          emergencyContact: response.data,
          relationship: {
            relationship: values.relationship,
            isPrimary: values.isPrimary,
          },
        };

        updateEmergencyContacts(emergencyContact as IEmergencyContact);
      }

      onClose();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const updateEmergencyContacts = (newContact: IEmergencyContact) => {
    const updatedList = (emergencyContactList ?? []).map((ec) => ({
      ...ec,
      relationship: {
        ...ec.relationship,
        isPrimary: newContact.relationship?.isPrimary
          ? false
          : ec.relationship?.isPrimary,
      },
    }));

    setValue('emergencyContacts', [...updatedList, newContact]);
  };

  const handleAddExistingSubmit = async (
    values: AddNewEmergencyContactForm,
  ) => {
    const emergencyContact = {
      emergencyContact: existingMasterName,
      relationship: {
        relationship: values.relationship,
        isPrimary: values.isPrimary,
      },
    };

    updateEmergencyContacts(emergencyContact as IEmergencyContact);

    updateToast({
      type: ConfirmTypes.SUCCESS,
      open: true,
      message:
        'Emergency contact successfully added. Remember to save the person record to finalize the update.',
    });
    onClose();
  };

  const handleEditSubmit = async (values: AddNewEmergencyContactForm) => {
    if (!currentEmergencyContact) {
      return;
    }

    const contactId = (
      currentEmergencyContact.emergencyContact as IMasterNamePerson
    )._id as string;

    const updatedList = (emergencyContactList ?? []).map((ec) => {
      const isEditedContact =
        typeof ec.emergencyContact === 'string'
          ? contactId === ec.emergencyContact
          : contactId === ec.emergencyContact._id;

      if (isEditedContact) {
        return {
          ...ec,
          relationship: {
            isPrimary: values.isPrimary,
            relationship: values.relationship,
          },
        };
      }

      return {
        ...ec,
        relationship: {
          ...ec.relationship,
          isPrimary: values.isPrimary ? false : ec.relationship?.isPrimary,
        },
      };
    });

    setValue('emergencyContacts', updatedList);
    updateToast({
      type: ConfirmTypes.SUCCESS,
      open: true,
      message:
        'Emergency contact successfuly edited. Remember to save the person record to finalize the update',
    });
    onClose();
  };

  const handleSubmit = async (values: AddNewEmergencyContactForm) => {
    if (currentEmergencyContact) {
      handleEditSubmit(values);
    } else if (existingMasterName) {
      handleAddExistingSubmit(values);
    } else {
      handleAddNewSubmit(values);
    }
  };

  const values = getValues();

  const excludedMasterNames =
    emergencyContactList?.map(
      (ec) => ec.emergencyContact as IMasterNamePerson,
    ) ?? [];

  const handleAddNew = () => {
    setIsMasterNameForm(true);
  };

  const handleBack = () => {
    onClose();
  };

  const handleSearchChange = async (newValue: IMasterName) => {
    if (!newValue._id) return;

    const { data: masterName } = await MasterIndexApi.getMasterPerson(
      newValue._id,
    );

    setExistingMasterName(masterName);
    methods.reset({
      phoneNumbers: masterName.phoneNumbers?.map(({ type, phoneNumber }) => ({
        type,
        phoneNumber: phoneNumber?.replace(/^\+1/, '') ?? '',
      })),
      firstName: masterName.firstName,
      lastName: masterName.lastName,
      emails:
        Array.isArray(masterName.emails) && masterName.emails.length > 0
          ? masterName.emails.map((email) =>
              typeof email === 'string' ? { email } : { email: '' },
            )
          : [{ email: '' }],
      middleName: masterName.middleName,
      addresses: masterName.addresses,
    });
  };

  return (
    <FormProvider {...methods}>
      <FormRoot
        sx={{ height: '70vh', padding: 0 }}
        onSubmit={methods.handleSubmit(handleSubmit)}
      >
        {!isMasterNameForm && !existingMasterName && (
          <Stack
            width="100%"
            sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}
          >
            <MasterNameAutocomplete
              excludedMasterNames={
                values
                  ? [values, ...excludedMasterNames]
                  : [...excludedMasterNames]
              }
              filterByType={MasterNameType.PERSON}
              onChange={(v) => handleSearchChange(v)}
              noResults={<NoResultItem />}
            />

            <Button
              color="error"
              type="button"
              size="sm"
              variant="secondary"
              className="w-fit text-sm"
              onClick={handleAddNew}
            >
              + Add new
            </Button>
          </Stack>
        )}

        {existingMasterName && (
          <>
            {EmergencyContactViewFields.map((item) => {
              let value = '';

              const emergencyContact = methods.getValues();

              switch (item.key) {
                case 'firstName':
                  value = emergencyContact.firstName;
                  break;
                case 'lastName':
                  value = emergencyContact.lastName;
                  break;
                case 'middleName':
                  value = emergencyContact.middleName;
                  break;
                case 'addresses':
                  value =
                    getAddressLocation(emergencyContact.addresses?.[0]) || '-';
                  break;
                case 'subAddress':
                  value = emergencyContact.addresses?.[0]?.subAddress ?? '-';
                  break;
                case 'email': {
                  const email = emergencyContact.emails?.[0];
                  value = email
                    ? typeof email === 'string'
                      ? email
                      : email.email
                    : '-';
                  break;
                }

                case 'phoneNumbers':
                  value =
                    emergencyContact.phoneNumbers?.[0]?.phoneNumber ?? '-';
                  break;
                default:
                  value = 'n/a';
              }

              return (
                <ViewItem
                  key={item.label}
                  label={item.label}
                  md={12}
                  value={value}
                />
              );
            })}
            <Divider sx={{ width: '100%' }} />
          </>
        )}

        {(isMasterNameForm || existingMasterName) &&
          EmergencyContactFormFields.filter(
            (field) => !field.isMasterNameField || !existingMasterName,
          ).map(({ grid, ...rest }: any, index: number) => {
            return rest.type === 'hidden' ? (
              <input
                key={index}
                type="hidden"
                {...methods.register(rest.name)}
              />
            ) : (
              <Grid key={index} xs={12} sm={6} md={1} {...grid} {...rest} item>
                {rest.name === 'phoneNumbers' ||
                rest.name === 'socialMedia' ||
                rest.name === 'emails' ||
                rest.name === 'addresses' ? (
                  createElement(specialFields[rest.name as string], rest)
                ) : (
                  <RenderFormField {...rest} />
                )}
              </Grid>
            );
          })}
      </FormRoot>

      <Divider sx={{ mt: 'auto', width: '100%' }} />
      <Stack
        columnGap={2}
        flexDirection="row"
        justifyContent="end"
        m={3}
        flex={1}
        flexGrow={0}
      >
        <MuiButton
          variant="contained"
          color="inherit"
          size="medium"
          onClick={handleBack}
        >
          Cancel
        </MuiButton>
        <MuiButton
          color="error"
          variant="contained"
          size="medium"
          type="submit"
          onClick={methods.handleSubmit(handleSubmit)}
        >
          Save
        </MuiButton>
      </Stack>
    </FormProvider>
  );
};
